import { setCookie } from "@/lib/setCookie";
import { ROLE_BOM, ROLE_OWNER } from "@/lib/utils";
import {
  reset,
  setCommunityData,
  setCurrentRole,
  setCurrentFinanceYear,
  setLoggedInUserData,
  setPrevCommunityData,
  setPropertyData,
  setTncStatus,
} from "@/redux/slices";
import { RootState } from "@/redux/store";
import { getOwnerBomBase, routes } from "@/routes/utils";
import { useGetBomUserCommunities, useGetEmployeeById } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import axiosAuth from "@/services/axiosAuth";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const useHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState<boolean>(false);
  const [isRoleMenuOpen, setIsRoleMenuOpen] = useState<boolean>(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState<boolean>(false);
  const [communityKey, setCommunityKey] = useState<any>("");

  const {loggedInUserData, communityData, currentRole, prevCommunityData, tncStatus, currentFinanceYear} = useSelector(
    (state: RootState) => state.currentData
  );

  const { data: bomUserCommunities, isLoading: isBomCommunityLoading } =
    useGetBomUserCommunities({
      enabled: loggedInUserData?.roles?.includes(ROLE_OWNER),
    });

  useEffect(() => {
    if (bomUserCommunities && loggedInUserData?.roles?.includes(ROLE_OWNER)) {
      let communityList = [...bomUserCommunities?.data]
      communityList?.sort((a: any, b: any) => Number(b.tncApproved) - Number(a.tncApproved));
      if (prevCommunityData?.communityData != communityData?.data) {
        dispatch(
          setCurrentRole(
            communityList?.[0]?.bom ? ROLE_OWNER : ROLE_OWNER
          )
        );
      }
      if (!communityData) {
        dispatch(setCommunityData(communityList?.[0]));
        dispatch(setPrevCommunityData(communityList?.[0]));
        setCommunityKey(Math.random() * 3);
      }
    }
  }, [bomUserCommunities, loggedInUserData]);

  useEffect(() => {
    if (tncStatus == "decline") {
      setCommunityKey(Math.random() * 3);
      setTimeout(() => {
        dispatch(setTncStatus(null));
      }, 100)
    }
  }, [tncStatus])

  const { data: employeeDetails, isLoading: isEmployeeDetailsLoading } =
    useGetEmployeeById({
      id: loggedInUserData?.userId as string,
      queryParams: {
        enabled: !!loggedInUserData?.userId,
      },
    });

  useEffect(() => {
    if (employeeDetails?.data) {
      dispatch(
        setLoggedInUserData({
          ...loggedInUserData,
          communityRoles: employeeDetails?.data?.employeeCommunityRole,
        })
      );
    }
  }, [employeeDetails]);

  const handleClose = () => setIsOpen(false);

  const onLogoutClick = () => {
    setIsLogoutOpen(true);
  };

  const onLogout = () => {
    // End the tracked server session (User Management > Active Sessions); logout proceeds regardless
    axiosAuth.post("auth/logout").catch(() => undefined);
    dispatch(reset());
    queryClient.clear();
    setCookie("accessToken", "");
    navigate(`/`);
  };

  const onCommunityChange = (e: any) => {
    const details = bomUserCommunities?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCurrentRole(details?.bom ? ROLE_OWNER : ROLE_OWNER));
    dispatch(setPrevCommunityData(communityData));
    dispatch(setCommunityData(details));
    dispatch(setPropertyData(null));
    const base =
      currentRole === ROLE_BOM ? routes.bom : getOwnerBomBase(location.pathname);
    navigate(`/${base}/${routes.dashboard}`);
  };

  const onYearChange = (year: string) => {
    dispatch(setCurrentFinanceYear(year));
  };

  const disabledCommunityDropdown = useMemo(() => {
    if (
      location.pathname == `/${routes.owner}/${routes.dashboard}` ||
      location.pathname == `/${routes.bom}/${routes.dashboard}`
    ) {
      return false;
    }
    return true;
  }, [location]);

  const onRoleChange = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRoleMenuOpen(false);
    dispatch(setPropertyData(null));
    const nextRole = currentRole == ROLE_BOM ? ROLE_OWNER : ROLE_BOM;
    dispatch(setCurrentRole(nextRole));
    navigate(
      `/${nextRole === ROLE_BOM ? routes.bom : routes.owner}/${routes.dashboard}`
    );
  };

  return {
    currentFinanceYear,
    onYearChange,
    isOpen,
    onLogoutClick,
    handleClose,
    setIsOpen,
    loggedInUserData,
    isLogoutOpen,
    setIsLogoutOpen,
    onLogout,
    isProfileMenuOpen,
    setIsProfileMenuOpen,
    onCommunityChange,
    bomUserCommunities,
    isBomCommunityLoading,
    disabledCommunityDropdown,
    isRoleMenuOpen,
    setIsRoleMenuOpen,
    onRoleChange,
    currentRole,
    communityData,
    isEmployeeDetailsLoading,
    communityKey
  };
};

export default useHeader;
